/**
 * FriskSpan — typed wrapper around an OpenTelemetry span with an in-memory
 * attribute mirror.
 *
 * Subclasses pass their `spanName` and `attributeFields` (public camelCase
 * field name -> telemetry attribute constant) to `super()` and expose a typed
 * `setAttributes({...})` API. Every write is validated against the field map,
 * mirrored into an in-memory record (keyed by constant), and written verbatim
 * to the underlying otel span. Span classes never format or transform values.
 */
import {
  type AttributeValue,
  context,
  type Span,
  type Tracer,
  trace,
} from '@opentelemetry/api'

export abstract class FriskSpan {
  private readonly _spanName: string
  private readonly _attributeFields: Readonly<Record<string, string>>
  private readonly _otelSpan: Span
  private readonly _attributes: Record<string, AttributeValue> = {}
  private _ended = false

  protected constructor({
    tracer,
    parent,
    spanName,
    attributeFields,
  }: {
    tracer: Tracer
    parent?: FriskSpan | null
    spanName: string
    attributeFields: Readonly<Record<string, string>>
  }) {
    this._spanName = spanName
    this._attributeFields = attributeFields
    const parentContext = parent
      ? trace.setSpan(context.active(), parent.otelSpan)
      : context.active()
    this._otelSpan = tracer.startSpan(this._spanName, undefined, parentContext)
  }

  /** The wrapped otel span. Internal to `core` (trace-context propagation). */
  get otelSpan(): Span {
    return this._otelSpan
  }

  /** The otel span id — for cross-span linkage without leaking otel types. */
  get spanId(): string {
    return this._otelSpan.spanContext().spanId
  }

  /** Set a single attribute by field name, e.g. `setAttribute('prompt', x)`. */
  setAttribute(field: string, value: AttributeValue): void {
    this.setAttributesByField({ [field]: value })
  }

  /** Read the in-memory value for `field`, or undefined if not set. */
  getAttribute(field: string): AttributeValue | undefined {
    return this._attributes[this.constantFor(field)]
  }

  /** End the underlying otel span. Subsequent calls are no-ops. */
  end(): void {
    if (this._ended) {
      return
    }
    this._ended = true
    this._otelSpan.end()
  }

  /** Worker for subclasses' typed `setAttributes`. Skips null/undefined. */
  protected setAttributesByField(
    values: Record<string, AttributeValue | null | undefined>,
  ): void {
    for (const [field, value] of Object.entries(values)) {
      const constant = this.constantFor(field)
      if (value === null || value === undefined) {
        continue
      }
      this._attributes[constant] = value
      this._otelSpan.setAttribute(constant, value)
    }
  }

  private constantFor(field: string): string {
    const constant = this._attributeFields[field]
    if (constant === undefined) {
      throw new Error(
        `Unknown attribute '${field}' for ${this.constructor.name}`,
      )
    }
    return constant
  }
}
